import $ from "../../libs/jquery-3.3.1.min.js"
import {CustomWiggle, gsap} from "../../libs/gsap/all.js"
import * as PIXI from "pixi.js";
import {VideoSource} from "pixi.js";
import 'pixi.js/math-extras'
import {BaseMiniGame} from "./BaseMiniGame";
import {GameD_Choice} from "./components/GameD_Choice";
import {AppearingInteractiveButton} from "./components/AppearingInteractiveButton";


// Game description : Quiz game - Image with choices
export class GameD extends BaseMiniGame {

  // Constructor
  constructor(data) {
    super(data);
    this.appWidth = this.data.appDimensions.width;
    this.appHeight = this.data.appDimensions.height;
    this.tagWidth = this.data.tagsSize.width;
    this.tagHeight = this.data.tagsSize.height;

    this.questions = this.data.questions;
    this.choicesObjects = [];
    this.currentQuestion = 0;

    this.clickTarget = false;
    this.isCheckingAnswer = false;
    this.currentTries = 0;
    this.maxTries = 99999;

    this.$screenEl = null;
  }

  async assetLoader() {
    await super.assetLoader();

    if (this.data.medias && this.data.medias.length) {
      for (let i = 0; i < this.data.medias.length; i++) {
        this.assets[this.data.medias[i].name] = await PIXI.Assets.load(this.data.medias[i].url);
      }
    }
  }

  async buildStage() {

    this.$screenEl = $(this.canvas).parents(".screen");

    // Enable events on the whole stage
    this.stage.hitArea = this.screen;

    this.questionTextContainer = this.$screenEl.find(".game-question");
    this.questionTextContainer.css({
      top: this.data.questionPosition.y,
      left: this.data.questionPosition.x,
    });

    const imageMargin = {x: 70, y: 80};
    const interactiveZoneSize = {width: 1610, height: 770};

    // Prepare image
    this.imageContainer = new PIXI.Container();
    this.imageContainer.alpha = 0;

    this.imageSpriteEN = new PIXI.Sprite();
    this.imageSpriteFR = new PIXI.Sprite();
    // this.imageSprite.anchor.set(0.5, 0); // center in x easier
    this.imageSpriteEN.width = this.imageSpriteFR.width = this.data.imageDimensions.width;
    this.imageSpriteEN.height = this.imageSpriteFR.height = this.data.imageDimensions.height;
    this.imageContainer.addChild(this.imageSpriteEN, this.imageSpriteFR);

    this.imageContainer.position.x = imageMargin.x;
    this.imageContainer.position.y = imageMargin.y;
    this.stage.addChild(this.imageContainer);

    // conclusion image
    if (this.data.conclusion.image || this.data.conclusion.video) {
      this.conclusionImageContainer = new PIXI.Container();

      // Define texture from video if available, or image otherwise.
      const type = this.data.conclusion.video ? "video" : "image";
      const textureFR = this.assets[this.data.conclusion[type].fr];
      const textureEN = this.assets[this.data.conclusion[type].en];
      this.conclusionImageSpriteEN = new PIXI.Sprite(textureEN);
      this.conclusionImageSpriteFR = new PIXI.Sprite(textureFR);
      this.conclusionVideos = this.getConclusionVideos();

      // Video - show replay overlay when video ended
      Object.keys(this.conclusionVideos).forEach((lang) => {
        this.conclusionVideos[lang].source.resource.pause();
        this.conclusionVideos[lang].source.resource.currentTime = 0;

        // Show replay overlay when video ended
        $(this.conclusionVideos[lang].source.resource).off("ended").on("ended", () => {
          this.$screenEl.find(".replay-overlay").fadeIn();
        });
      })

      this.conclusionImageSpriteEN.width = this.conclusionImageSpriteFR.width = this.data.imageDimensions.width;
      this.conclusionImageSpriteEN.height = this.conclusionImageSpriteFR.height = this.data.imageDimensions.height;
      this.conclusionImageContainer.addChild(this.conclusionImageSpriteEN, this.conclusionImageSpriteFR);

      this.conclusionImageContainer.position.x = imageMargin.x;
      this.conclusionImageContainer.position.y = imageMargin.y;
      this.stage.addChild(this.conclusionImageContainer);
    }


    const rightContainerWidth = interactiveZoneSize.width - this.data.imageDimensions.width;
    const rightContainerPosition = {
      x: this.imageContainer.position.x + this.data.imageDimensions.width + 20,
      y: this.imageContainer.position.y
    };

    const baseTagsX = (rightContainerPosition.x + rightContainerWidth / 2) - imageMargin.x;
    const baseTagsY = this.data.tagsPosition.y;

    // Prepare choices
    this.choicesContainer = new PIXI.Container();
    this.choicesContainer.position.x = this.imageContainer.position.x;
    this.choicesContainer.position.y = this.imageContainer.position.y;
    this.stage.addChild(this.choicesContainer);

    for (let i = 0; i < this.data.choicesCount; i++) {
      const spaceBetween = 25;
      let choice = new GameD_Choice(baseTagsX, baseTagsY + (i * this.data.tagsSize.height) + (i * spaceBetween), {id: i + 1}, this.data.choiceStyle, this);
      choice.on("selected", this.highlightAnswer.bind(this));
      this.choicesContainer.addChild(choice);
      this.choicesObjects.push(choice);
    }

    // Add validation button
    const checkmark = new PIXI.Sprite(this.assets["checkmark"]);
    const checkmarkDisabled = new PIXI.Sprite(this.assets["checkmark-disabled"]);
    this.validateButton = new AppearingInteractiveButton(75, 75, checkmark, checkmarkDisabled, this.checkAnswer.bind(this), false);

    let lastChoice = this.choicesObjects[this.choicesObjects.length - 1];
    this.validateButton.position.set(rightContainerPosition.x + (rightContainerWidth / 2), (lastChoice.position.y + lastChoice.height + 60) + (checkmark.height / 2));

    this.stage.addChild(this.validateButton);

    // Start
    this.swapQuestion();
  }

  // Init conclusion videos
  getConclusionVideos() {
    const videoTextures = {};
    if (this.conclusionImageSpriteEN && this.conclusionImageSpriteFR) {
      [this.conclusionImageSpriteEN, this.conclusionImageSpriteFR].forEach((sprite, index) => {
        if (sprite.texture.source && sprite.texture.source instanceof VideoSource) {
          videoTextures[index === 0 ? "en" : "fr"] = {
            "sprite": sprite,
            "source": sprite.texture.source
          };
        }
      });
    }
    return videoTextures;
  }

  // Replay conclusion videos
  replayConclusionVideo(pause = false) {
    if (this.conclusionVideos) {
      this.$screenEl.find(".replay-overlay").fadeOut();
      Object.keys(this.conclusionVideos).forEach((lang) => {
        // Only replay current language video
        if (lang === app.i18n.lang) {
          this.conclusionVideos[lang].sprite.alpha = 1.0;
          this.conclusionVideos[lang].source.resource.currentTime = 0;
          if (!pause) {
            this.conclusionVideos[lang].source.resource.play();
          }
        } else {
          // Hide unused texture
          this.conclusionVideos[lang].sprite.alpha = 0.0;
        }
      });
    }
  }

  // Stop conclusion videos
  stopConclusionVideos() {
    if (this.conclusionVideos) {
      this.$screenEl.find(".replay-overlay").fadeOut();
      Object.keys(this.conclusionVideos).forEach((lang) => {
        this.conclusionVideos[lang].source.resource.pause();
        this.conclusionVideos[lang].source.resource.currentTime = 0;
      })
    }
  }

  nextQuestion(resetState) {
    resetState = typeof resetState !== "undefined" ? resetState : false;

    this.currentQuestion++;

    if (this.currentQuestion >= this.questions.length) {
      // we went through all the questions, go to end
      console.log("All questions answered!");
      this.goToConclusion();
      return;
    }

    if (resetState) {
      this.hideQuestion(() => {
        this.swapQuestion();

        this.showQuestion(() => {
          this.resetQuestionState();
        });
      });
    }

    this.currentTries = 0;

    this.isCheckingAnswer = false;
  }

  hideQuestion(callback) {
    let questionChangeTimeline = gsap.timeline({
      onComplete: () => {
        if (typeof callback === "function") {
          console.log("complete, calling callback");
          callback();
        }
      }
    });

    // Animate Out
    questionChangeTimeline.to(this.questionTextContainer, {
      alpha: 0.0,
      duration: 0.5,
      ease: "power1.inOut",
    });

    for (let i = 0; i < this.choicesObjects.length; i++) {
      this.choicesObjects[i].disable();

      questionChangeTimeline.to(this.choicesObjects[i], {
        pixi: {
          alpha: 0,
        },
        duration: 0.5,
        ease: "power1.inOut",
      }, 0);
    }
  }

  showQuestion(callback) {
    let questionChangeTimeline = gsap.timeline({
      onComplete: () => {
        if (typeof callback === "function") {
          callback();
        }
      }
    });

    // Animate in
    questionChangeTimeline.to(this.questionTextContainer, {
      alpha: 1.0,
      duration: 0.5,
      ease: "power1.inOut",
    });

    questionChangeTimeline.to(this.imageContainer, {
      pixi: {
        alpha: 1,
      },
      duration: 0.5,
      ease: "power1.inOut",
    }, "<=");

    for (let i = 0; i < this.choicesObjects.length; i++) {
      questionChangeTimeline.fromTo(this.choicesObjects[i], {
        pixi: {
          y: this.choicesObjects[i].originalPosition.y + 40,
          alpha: 0,
        },
      }, {
        pixi: {
          y: this.choicesObjects[i].originalPosition.y,
          alpha: 1,
        },
        duration: 0.5,
        ease: "power1.inOut",
      }, "-=0.3");
    }

    questionChangeTimeline.add(() => {
      // Show validate button but disabled
      this.validateButton.show(false);
    }, "+=0.25");
  }

  swapQuestion() {
    const question = this.questions[this.currentQuestion];

    this.imageSpriteEN.texture = this.assets[question.image.en];
    this.imageSpriteFR.texture = this.assets[question.image.fr];

    this.questionTextContainer.find(".en").html(question.question["en"]);
    this.questionTextContainer.find(".fr").html(question.question["fr"]);

    for (let i = 0; i < question.choices.length; i++) {
      this.choicesObjects[i].setText({
        en: question.choices[i]["en"],
        fr: question.choices[i]["fr"]
      });
    }
  }

  retryQuestion() {
    this.resetQuestionState();
    this.isCheckingAnswer = false;
  }

  resetQuestionState() {
    for (let i = 0; i < this.choicesObjects.length; i++) {
      this.choicesObjects[i].enable();
      this.choicesObjects[i].hideAnswerMarker();

      gsap.to(this.choicesObjects[i], {
        pixi: {
          alpha: 1,
        },
        duration: 0.25,
        ease: "power1.inOut",
      });
    }
  }

  highlightAnswer(choice) {
    for (let i = 0; i < this.choicesObjects.length; i++) {
      this.choicesObjects[i].showSelected(this.choicesObjects[i].id === choice.id)
    }

    this.validateButton.enable();
  }

  checkAnswer() {
    if (this.isCheckingAnswer) return;

    let choice = null;
    for (let i = 0; i < this.choicesObjects.length; i++) {
      this.choicesObjects[i].disable();

      if (this.choicesObjects[i].isSelected) {
        choice = this.choicesObjects[i];
      }
    }

    this.isCheckingAnswer = true;
    this.currentTries++;

    if (this.questions[this.currentQuestion].answer == choice.id) {
      // good answer
      this.validateButton.hide();

      choice.showAnswerMarker(true);

      // for (let i = 0; i < this.choicesObjects.length; i++) {
      //   if (this.choicesObjects[i].id != choice.id) {
      //     gsap.to(this.choicesObjects[i], {
      //       pixi: {
      //         alpha: 0.5,
      //       },
      //       duration: 0.25,
      //       ease: "power1.inOut",
      //     });
      //   }
      // }

      console.log("Go to next question");
      setTimeout(this.nextQuestion.bind(this, true), 2000);
    } else {
      // bad answer

      console.log("BAD!");
      this.validateButton.disable();

      choice.showAnswerMarker(false);

      // Shake animation before placing back the tag
      gsap.to(choice, {
        x: choice.position.x + 10,
        duration: 0.50,
        ease: CustomWiggle.create("wrongAnswerWiggle", {
          wiggles: 4,
          type: "easeOut"
        }),
        onComplete: () => {
          if (this.currentTries >= this.maxTries) {
            // enough tries, go to next question
            console.log("enough tries, go to next question");
            setTimeout(this.nextQuestion.bind(this, true), 2000);
          } else {
            console.log("retry question");
            setTimeout(this.retryQuestion.bind(this), 2000);
          }
        }
      });

    }
  }

  buildConclusionTimeline() {

    this.conclusionTimeline = gsap.timeline({paused: true});

    this.conclusionTimeline.addLabel("fade-out-question");

    this.conclusionTimeline.add(() => {
      this.replayConclusionVideo(true);
    });

    this.conclusionTimeline.to(this.questionTextContainer, {
      alpha: 0.0,
      duration: 0.25,
      delay: 0.70,
      ease: "power1.inOut"
    }, "fade-out-question");

    this.conclusionTimeline.to(this.choicesObjects, {
      pixi: {
        alpha: 0,
      },
      duration: 0.25,
      delay: 0.70,
      ease: "power1.inOut",
    }, "fade-out-question");

    if (this.conclusionImageContainer) {
      this.conclusionTimeline.to(this.imageContainer, {
        pixi: {
          alpha: 0,
        },
        duration: 0.25,
        delay: 0.70,
        ease: "power1.inOut",
      }, "fade-out-question");
    }

    // force game conclusion to be active
    this.conclusionTimeline.set(this.$screenEl.find(".game-conclusion"), {
      alpha: 1,
    });

    if (this.$screenEl.find(".container-title").length) {
      this.conclusionTimeline.fromTo(this.$screenEl.find(".container-title"), {
        '--title-width': "0%"
      }, {
        '--title-width': "100%",
        duration: 0.45,
        ease: "power2.out",
        immediateRender: true
      });

      this.conclusionTimeline.fromTo(this.$screenEl.find(".container-title-inner"), {
        alpha: 0.0
      }, {
        alpha: 1.0,
        duration: 0.30,
        ease: "power1.in",
        immediateRender: true
      });
    }

    this.conclusionTimeline.fromTo(this.$screenEl.find('.container-content'), {
      alpha: 0,
    }, {
      alpha: 1,
      ease: "power1.inOut",
      duration: 0.30,
      immediateRender: true
    });

    if (this.conclusionImageContainer) {
      this.conclusionTimeline.fromTo(this.conclusionImageContainer, {
          pixi: {
            alpha: 0,
          }
        },
        {
          pixi: {
            alpha: 1,
          },
          duration: 0.25,
          ease: "power1.inOut",
        }, "<=");
    }

    // Play video
    this.conclusionTimeline.add(() => {
      this.replayConclusionVideo();
    });

    if (this.$screenEl.find(".container-note").length) {
      this.conclusionTimeline.fromTo(this.$screenEl.find(".container-note"), {
        y: "+=100%"
      }, {
        y: 0,
        duration: 1.50,
        ease: "power1.inOut",
        immediateRender: true
      }, "+=0.75");

      this.conclusionTimeline.fromTo(this.$screenEl.find(".container-note"), {
        autoAlpha: 0
      }, {
        autoAlpha: 1,
        duration: 0.1,
        ease: "linear",
        immediateRender: true
      }, '<=');
    }

    // Show container button
    if (this.$screenEl.find(".container-button").length) {
      this.conclusionTimeline.fromTo(this.$screenEl.find(".container-button"), {
        alpha: 0.0,
        scale: 0.0
      }, {
        alpha: 1.0,
        scale: 1.0,
        duration: 0.75,
        ease: "power2.out",
        immediateRender: true,
        onStart: () => {
          // Disable button animations
          this.$screenEl.find("button.pulse-animation, button.up-down-animation").removeClass("play-animation");
        },
        onComplete: () => {
          this.$screenEl.find(".game-conclusion").removeClass("disabled");

          // Enable button animations
          this.$screenEl.find("button.pulse-animation, button.up-down-animation").addClass("play-animation");
        }
      });

      // Disable click on this section
      this.$screenEl.find(".game-conclusion").addClass("disabled");
    }

  }

  goToConclusion() {
    this.conclusionTimeline.play(0);
  }

  beforeAnimateIn() {
    // Make sure everything is well hidden
    gsap.set(this.questionTextContainer, {
      alpha: 0
    });

    gsap.set(this.choicesObjects, {
      pixi: {
        alpha: 0,
      },
    });

    // rebuild timeline
    if (this.conclusionTimeline) {
      this.conclusionTimeline.pause();
    } else {
      this.buildConclusionTimeline();
    }
  }

  begin() {

    this.swapQuestion();

    this.showQuestion(() => {
      console.log("show done", this);
      this.resetQuestionState();
    });

  }

  reset() {
    //console.log("gameD Reset");

    this.currentQuestion = 0;
    this.currentTries = 0;
    this.isCheckingAnswer = false;

    this.stopConclusionVideos();
    this.swapQuestion();

    this.validateButton.hide();

    for (let i = 0; i < this.choicesObjects.length; i++) {
      this.choicesObjects[i].reset();
    }

    // rebuild timeline
    if (this.conclusionTimeline) {
      this.conclusionTimeline.kill();
      this.conclusionTimeline = null;
    }
    this.buildConclusionTimeline();
  }

  interfaceLanguageSwitch(lang) {
    super.interfaceLanguageSwitch(lang);

    const imageShown = lang === "fr" ? this.imageSpriteFR : this.imageSpriteEN;
    const imageHidden = lang === "fr" ? this.imageSpriteEN : this.imageSpriteFR;
    imageShown.alpha = 1;
    imageHidden.alpha = 0;

    if (this.data.conclusion.image) {
      const conclusionImageShown = lang === "fr" ? this.conclusionImageSpriteFR : this.conclusionImageSpriteEN;
      const conclusionImageHidden = lang === "fr" ? this.conclusionImageSpriteEN : this.conclusionImageSpriteFR;
      conclusionImageShown.alpha = 1;
      conclusionImageHidden.alpha = 0;
    }
  }

}
